import React from "react";
import { motion } from "framer-motion";
import { Database, Cpu, Gauge, RefreshCcw } from "lucide-react";
import { cn } from "@/lib/utils";

const NODES = [
  { icon: Database, label: "Data", angle: 0 },
  { icon: Cpu, label: "Model", angle: 90 },
  { icon: Gauge, label: "Score", angle: 180 },
  { icon: RefreshCcw, label: "Improvement", angle: 270 },
];

/**
 * ContinuousImprovementCycle — a circular data → model → score → improvement loop.
 * The connector ring has a subtle continuously-rotating gradient stroke.
 */
export const ContinuousImprovementCycle = ({ size = 340, className }) => {
  const radius = size / 2 - 44;
  const cx = size / 2;
  const cy = size / 2;

  return (
    <div
      className={cn("relative flex items-center justify-center", className)}
      style={{ width: size, height: size }}
      data-testid="continuous-improvement-cycle"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id="ring-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00E676" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#00E676" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#00E676" stopOpacity="0.9" />
          </linearGradient>
        </defs>

        {/* Base track */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="1.5"
        />

        {/* Rotating gradient arc */}
        <motion.circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="url(#ring-grad)"
          strokeWidth="2"
          strokeDasharray={`${Math.PI * radius * 0.55} ${Math.PI * radius * 2}`}
          strokeLinecap="round"
          style={{ transformOrigin: `${cx}px ${cy}px` }}
          animate={{ rotate: 360 }}
          transition={{ duration: 12, ease: "linear", repeat: Infinity }}
        />

        {/* Small emerald dot orbiting */}
        <motion.circle
          cx={cx + radius}
          cy={cy}
          r="4"
          fill="#00E676"
          style={{
            transformOrigin: `${cx}px ${cy}px`,
            filter: "drop-shadow(0 0 6px rgba(0,230,118,0.8))",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 6, ease: "linear", repeat: Infinity }}
        />
      </svg>

      {/* Nodes positioned around the ring */}
      {NODES.map((n, i) => {
        const Icon = n.icon;
        const rad = (n.angle * Math.PI) / 180;
        const x = cx + Math.cos(rad) * radius - 30;
        const y = cy + Math.sin(rad) * radius - 30;
        return (
          <motion.div
            key={n.label}
            initial={{ opacity: 0, scale: 0.4 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay: 0.15 * i, duration: 0.5 }}
            className="absolute flex h-16 w-16 flex-col items-center justify-center rounded-2xl border border-white/[0.08] bg-bg-raised text-brand-emerald shadow-[0_6px_18px_rgba(0,0,0,0.5)]"
            style={{ left: x, top: y }}
          >
            <Icon className="h-5 w-5" strokeWidth={1.75} />
            <span className="mt-0.5 text-[9px] font-medium uppercase tracking-widest text-white">
              {n.label}
            </span>
          </motion.div>
        );
      })}

      {/* Center label */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-muted">
            Continuous
          </div>
          <div className="font-display text-lg font-semibold text-white">
            Learning Loop
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContinuousImprovementCycle;
